import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { supabase } from '@/utils/supabaseClient';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [supabaseUser, setSupabaseUser] = useState<any>(null);
  
  const { user: reduxUser, isAuthenticated } = useSelector((state: RootState) => state.auth);

  // Check auth state more reliably
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const checkAuth = async () => {
      try {
        // Set a timeout to prevent infinite loading
        timeoutId = setTimeout(() => {
          console.warn('Auth check timed out, falling back to loading complete');
          setIsLoading(false);
        }, 3000); // 3 second timeout

        const { data: { session }, error } = await supabase.auth.getSession();
        
        // Clear timeout since we got a response
        clearTimeout(timeoutId);
        
        if (error) {
          console.error('Auth session error:', error);
          setSupabaseUser(null);
        } else {
          setSupabaseUser(session?.user || null);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        clearTimeout(timeoutId);
        setSupabaseUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();

    // Listen for auth changes with error handling
    let subscription: any;
    try {
      const { data } = supabase.auth.onAuthStateChange((_event, session) => {
        setSupabaseUser(session?.user || null);
        setIsLoading(false);
      });
      subscription = data;
    } catch (error) {
      console.error('Failed to setup auth listener:', error);
      // Don't let auth listener failure block the app
      setIsLoading(false);
    }

    return () => {
      clearTimeout(timeoutId);
      if (subscription?.subscription) {
        subscription.subscription.unsubscribe();
      }
    };
  }, []);
  
  // User is authenticated if either source has user data
  // Prioritize Redux state if Supabase WebSocket is having issues
  const isUserAuthenticated = (reduxUser && isAuthenticated) || supabaseUser;

  console.log('ProtectedRoute auth check:', {
    supabaseUser: !!supabaseUser,
    reduxUser: !!reduxUser,
    isAuthenticated,
    isUserAuthenticated: !!isUserAuthenticated,
    isLoading,
    location: location.pathname
  });

  // Show loading spinner while auth state is initializing
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isUserAuthenticated) {
    // Save the intended destination before redirecting to login
    localStorage.setItem('intendedDestination', location.pathname + location.search);
    console.log('Redirecting to login, saved destination:', location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute; 